import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { FC, useState } from "react";

import { SiteByIdDTO, UserDTO, addDataProvider } from "api";

import { useNotify } from "hooks";

import { QUERY_KEYS } from "consts";

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  users: UserDTO[];
  site: SiteByIdDTO;
}

export const UserModal: FC<UserModalProps> = ({
  isOpen,
  onClose,
  users,
  site,
}) => {

  // ---- Hook ----
  const notify = useNotify();

  // ---- State ----
  const [values, setValues] = useState<any>({});
  const [availableDataProviders, setAvailableDataProviders] = useState(() => {
    if (Array.isArray(users)) {
      return users.map((u) => ({
        ...u,
        fullName: `${u.firstName || ""} ${u.lastName || ""}`,
        providerId: u.id,
      }));
    }
    return [];
  });

  // ---- API Call ----
  const useAddDataProvider = () => {
    const queryClient = useQueryClient();
  
    const mutationFn = ({ siteId, userId }: { siteId: number; userId: number }) =>
      addDataProvider(siteId, userId);
  
    return useMutation(mutationFn, {
      onSuccess: () => {
        notify.success('Data provider successfully added!');
        queryClient.invalidateQueries([QUERY_KEYS.SITES]);
      },
      onError: () => {
        notify.error('Some error has occurred while adding the data provider!');
      },
    });
  };

  const { mutate: addDataProviderRequest } = useAddDataProvider();

  // ---- Handle Add Data Provider ----
  const handleAddProvider = async () => {
    const selectedDataProviderId = values["Data Provider"]?.id;
   
    addDataProviderRequest(
      {
        siteId: site?.id,
        userId: selectedDataProviderId,
      },
      {
        onSuccess: () => {
          setAvailableDataProviders((prevDataProviders) =>
            prevDataProviders.filter(
              (provider: any) => provider.providerId !== selectedDataProviderId
            )
          );
        },
      }
    );
    setValues({});
    onClose();
  };

  // ---- Handle Close Popup Function ----
  const handleClose = () => {
    setValues({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Add Data Provider</DialogTitle>
      <DialogContent>
        <form onSubmit={(e) => e.preventDefault()}>
          <FormControl sx={{ minWidth: 500 }}>
            <Select
              value={values["Data Provider"]?.providerId || ""}
              name="Data Provider"
              onChange={(e) => {
                const selectedUserId = e.target.value;
                const selectedUser = availableDataProviders.find(
                  (user: any) => user.providerId === selectedUserId,
                );
                setValues({
                  ...values,
                  [e.target.name]: selectedUser,
                });
              }}
              label="Data Provider"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {availableDataProviders.map((option: any) => (
                <MenuItem key={option.providerId} value={option.providerId}>
                  {option.fullName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleAddProvider} variant="contained" color="primary">
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
